/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setSummary as setS, setIsLoading } from './summary.slice';
import { useGetSummaryMutation } from './summary.api';
import { getTicketsSelectedMergeByTicketType } from '../tickets-selected/utils';
import { debounce } from 'lodash';
import { useAddOns, useMsci } from '@/store';
import useAddOnsConfig from '@/hooks/useAddOnsConfig';
import useAddOnsControl from '@/sections/addons/useAddOnsControl';
import useMsciControl from '@/sections/pay/useMsciControl';

export default function useSummaryUpdate(path = '') {
  const dispatch = useDispatch();
  const [orderSummaryRequest] = useGetSummaryMutation();
  const booking = useSelector((state) => state.booking);
  const summary = useSelector((state) => state.summary);
  const ticketsSelected = useSelector(
    (state) =>
      state.ticketsSelected.ids.map((id) => state.ticketsSelected.entities[id]),
    shallowEqual,
  );

  const {
    ticketInsurance: { disabled },
  } = useAddOns();

  const {
    msci: { availableBin, selectedBin, ...restMsci },
  } = useMsci();

  const { isEventWithTicketInsurance } = useAddOnsConfig();

  const { handleTicketInsurancePrice, verifyData } = useAddOnsControl();
  const { setAvailablePlans } = useMsciControl();

  const promotions = useSelector((state) => state.promotions, shallowEqual);
  const { method: payment } = useSelector(
    (state) => state.payment,
    shallowEqual,
  );

  const {
    event: { id },
    preferences: { fee_breakdown },
  } = useSelector((state) => state.event || { event: {}, preferences: {} });

  const ticketInsurance = useSelector(
    (state) => state.addons.ticketInsurance,
    shallowEqual,
  );

  const setSummary = (summary) => {
    dispatch(setS(summary));
  };

  const getAddOns = () => {
    const addOns = [];

    addOns.push({
      kind: 'ticket_insurance',
      selected: ticketInsurance.selected,
    });

    if (addOns.length < 1) {
      return null;
    }

    return addOns;
  };

  const getBin = () =>
    selectedBin
      ? {
          bin: {
            number: String(selectedBin),
            ...(restMsci?.selected
              ? {
                  installment: String(restMsci.selected.payment_plan),
                  payment_plan_kind: restMsci.selected.payment_type,
                  selected: false,
                }
              : {}),
          },
        }
      : {};

  const updateSummary = async (
    paymentMethod = 'Gateway::Banwire',
    ticketsSelection,
    promotionCode,
    eventId,
    bookingCurrent,
  ) => {
    const tickets = getTicketsSelectedMergeByTicketType(ticketsSelection);

    const body = Object.values(tickets).map(
      ({ ticket_type_id, quantity, ticket_type_price }) => ({
        ticket_Type_id: ticket_type_id,
        quantity_required: quantity,
        price_per_unit: ticket_type_price,
      }),
    );

    const request = {
      booking_id: bookingCurrent.orderInfo?.id,
      promotion_code: promotionCode,
      event_id: eventId,
      fee_breakdown,
      payment_method_type: paymentMethod,
      body,
      ...getBin(),
    };

    const requestWithAddOns = {
      ...request,
      addOns: getAddOns(),
    };

    const resp = await orderSummaryRequest(
      !disabled ? requestWithAddOns : request,
    );

    if (!disabled) {
      verifyData(resp.data, path);
      handleTicketInsurancePrice(resp.data);
    }

    if (resp?.data?.payment_plan_available) {
      setAvailablePlans(resp.data?.payment_plan_available);
    }

    setSummary({ ...resp.data, isLoading: false });
  };

  const reload = (
    tickets,
    promotionCode,
    currentPayment,
    eventId,
    bookingCurrent,
  ) => {
    if (tickets && tickets.length > 0 && currentPayment) {
      const method = Object.keys(currentPayment)[0];
      updateSummary(
        method,
        tickets,
        promotionCode,
        eventId,
        bookingCurrent,
      ).then();
    }
  };

  const debounceReload = useCallback(debounce(reload, 300), []);

  const getSummaryUpdate = () => {
    reload(
      ticketsSelected,
      promotions ? promotions.code : null,
      payment,
      id,
      booking,
    );
  };
  const getDebouncedSummary = () => {
    debounceReload(
      ticketsSelected,
      promotions ? promotions.code : null,
      payment,
      id,
      booking,
    );
  };

  return {
    booking,
    summary,
    getSummaryUpdate,
    getDebouncedSummary,
  };
}
